import type { TimingFeedback, TimingNoteFeedback } from '@tunasong/music-lib'
import type { FC } from 'react'
import { useVocalCopy } from '../pitch-training/use-vocal-copy.js'
import { TheoryGlyphs } from '../theory-copy/theory-glyphs.js'
import styles from './timing-feedback-panel.module.css'

/**
 * Everything the tool says about WHEN the notes were played.
 *
 * Not one sentence here is composed by this file. Each verdict is one §10 lexicon entry, chosen by
 * `toTimingFeedback` from the numbers `scoreTiming` measured, and rendered with its `{{ms}}` and
 * `{{r}}` filled through the never-leak-raw-token guard. The only strings this component owns are
 * the section label and a numeral.
 *
 * Order is the message, and the caveat comes FIRST. Every chip below it reads as an absolute claim
 * unless the reader has already been told what the numbers are measured against - "a touch early"
 * is heard as "early against the beat", which is exactly the claim this tool gave up when the
 * input-latency calibration was removed. `timing-relative-only` is shown on every timing verdict
 * for that reason, and it is a locked lexicon entry like the rest. The feel sentence comes last
 * because it is the most specialised of the three and only appears when the melody has a feel.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6.2, §10
 */

interface NoteChipProps {
  note: TimingNoteFeedback
  position: number
}

const NoteChip: FC<NoteChipProps> = ({ note, position }) => {
  const band = useVocalCopy(note.bandLexiconId, { ms: note.ms })
  return (
    <li className={styles.chip}>
      <span className={styles.chipIndex}>{position + 1}</span>
      {band ? (
        <span className={styles.chipBand} title={band.body}>
          <TheoryGlyphs>{band.tag}</TheoryGlyphs>
        </span>
      ) : null}
    </li>
  )
}

interface CopyLineProps {
  lexiconId: string
  ms?: number
  r?: number
  className: string
}

const CopyLine: FC<CopyLineProps> = ({ lexiconId, ms, r, className }) => {
  const copy = useVocalCopy(lexiconId, { ms, r })
  if (!copy) {
    return null
  }
  return (
    <p className={className}>
      <TheoryGlyphs>{copy.body}</TheoryGlyphs>
    </p>
  )
}

export interface TimingFeedbackPanelProps {
  /** Null when the aligner paired nothing - there is no timing to report, so nothing renders. */
  feedback: TimingFeedback | null
}

export const TimingFeedbackPanel: FC<TimingFeedbackPanelProps> = ({ feedback }) => {
  if (feedback === null) {
    return null
  }

  return (
    <section className={styles.timing} aria-label="Timing">
      <CopyLine lexiconId="timing-relative-only" className={styles.caveat} />

      <ol className={styles.chips}>
        {feedback.perNote.map((note, position) => (
          <NoteChip key={position} note={note} position={position} />
        ))}
      </ol>

      {feedback.feelLexiconId ? (
        <CopyLine lexiconId={feedback.feelLexiconId} r={feedback.feelR ?? undefined} className={styles.body} />
      ) : null}
    </section>
  )
}
